import React from 'react';
import { SEO } from '../components/seo/SEO';
import { Heart, Calendar, Truck, Camera, PawPrint, Phone, Mail, CheckCircle2, AlertTriangle, Users, Clock, BookOpen } from 'lucide-react';
import { getBreadcrumbSchema } from '../lib/jsonLd';

const VOLUNTEER_ROLES = [
  {
    icon: <Heart className="w-8 h-8 text-accessible-red-600" />,
    title: "Opieka nad zwierzętami",
    description: "Pomoc w codziennej opiece, karmienie, sprzątanie, spacery z psami, socjalizacja.",
    requirements: [
      "Minimum 4 godziny tygodniowo",
      "Regularne zaangażowanie",
      "Umiejętność pracy w zespole",
      "Cierpliwość i empatia"
    ]
  },
  {
    icon: <Calendar className="w-8 h-8 text-accessible-red-600" />,
    title: "Wydarzenia i akcje",
    description: "Organizacja i obsługa wydarzeń adopcyjnych, zbiórek, akcji promocyjnych.",
    requirements: [
      "Dyspozycyjność w weekendy",
      "Umiejętności organizacyjne",
      "Komunikatywność",
      "Kreatywność"
    ]
  },
  {
    icon: <Truck className="w-8 h-8 text-accessible-red-600" />,
    title: "Transport",
    description: "Pomoc w transporcie zwierząt do weterynarza, na wydarzenia adopcyjne.",
    requirements: [
      "Prawo jazdy kat. B",
      "Własny samochód",
      "Doświadczenie w przewozie zwierząt",
      "Elastyczność czasowa"
    ]
  },
  {
    icon: <Camera className="w-8 h-8 text-accessible-red-600" />,
    title: "Media i promocja",
    description: "Fotografia, prowadzenie social mediów, pisanie tekstów, grafika.",
    requirements: [
      "Umiejętności fotograficzne/graficzne",
      "Znajomość social mediów",
      "Dobra organizacja pracy",
      "Kreatywność"
    ]
  }
];

export function VolunteerPage() {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Wolontariat', url: 'https://znajdki.pl/wolontariat' }
  ]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <SEO 
        title="Zostań wolontariuszem - Fundacja Znajdki"
        description="Dołącz do zespołu wolontariuszy w Fundacji Znajdki. Pomagaj przy opiece nad zwierzętami, wydarzeniach adopcyjnych lub transporcie. Każde zaangażowanie ma znaczenie!"
        url="/wolontariat"
        image="/images/animals/fundacja-znajdki-zwierzeta-001.webp"
        jsonLd={breadcrumbSchema}
      />

      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="heading-1 mb-6">
          Zostań wolontariuszem
        </h1>
        <div className="relative w-full max-w-4xl mx-auto mb-12 rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="/images/animals/fundacja-znajdki-zwierzeta-004.webp"
            alt="Wolontariusze z psami – Fundacja Znajdki"
            className="w-full h-64 sm:h-72 md:h-80 lg:h-[500px] xl:h-[600px] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <p className="heading-4-white font-medium max-w-2xl mx-auto no-margin">
              Dołącz do naszego zespołu i pomóż zwierzakom znaleźć nowy dom
            </p>
          </div>
        </div>
        <p className="text-xl text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white max-w-3xl mx-auto">
          Jako wolontariusz możesz realnie wpłynąć na los bezdomnych zwierząt. Każda para rąk się przyda!
        </p>
      </div>

      {/* W prostym języku */}
      <section className="mb-8">
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
          <h2 className="heading-4 no-margin">W skrócie</h2>
          <p className="body-medium no-margin mt-2">Zgłaszasz chęć pomocy, poznajesz zasady i zakres działań. Wspierasz zwierzęta według swoich możliwości czasowych.</p>
        </div>
      </section>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Column */}
        <div className="space-y-8">
          {/* Why Volunteer */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-accessible-red-600" />
              </div>
              <h2 className="heading-3">
                Dlaczego warto?
              </h2>
            </div>
            
            <div className="space-y-4">
              <p className="body-medium">
                Wolontariat w Fundacji Znajdki to nie tylko pomoc zwierzętom, ale także:
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span className="body-medium no-margin">Możliwość zdobycia cennego doświadczenia</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span className="body-medium no-margin">Poznanie wspaniałych ludzi o podobnych wartościach</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span className="body-medium no-margin">Satysfakcja z niesienia pomocy</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span className="body-medium no-margin">Rozwój osobisty i nowe umiejętności</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-accessible-red-600" />
              </div>
              <h2 className="heading-3">
                Wymagania ogólne
              </h2>
            </div>
            
            <div className="space-y-4">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block label-large">Wiek minimum 16 lat</strong>
                    <span className="body-medium no-margin">Osoby niepełnoletnie potrzebują zgody rodziców/opiekunów</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block label-large">Regularne zaangażowanie</strong>
                    <span className="body-medium no-margin">Oczekujemy systematycznej pomocy według ustalonego grafiku</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block label-large">Odpowiedzialność</strong>
                    <span className="body-medium no-margin">Punktualność i rzetelne wykonywanie powierzonych zadań</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block label-large">Praca zespołowa</strong>
                    <span className="body-medium no-margin">Umiejętność współpracy z innymi wolontariuszami</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Column - Volunteer Roles */}
        <div className="space-y-8">
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <PawPrint className="w-6 h-6 text-accessible-red-600" />
              </div>
              <h2 className="heading-3">
                Obszary wolontariatu
              </h2>
            </div>
            
            <div className="space-y-8">
              {VOLUNTEER_ROLES.map((role, index) => (
                <div key={index} className="border-b border-accessible-gray-200 dark:border-accessible-gray-700 last:border-0 pb-8 last:pb-0">
                  <div className="flex items-center gap-4 mb-4">
                    {role.icon}
                    <h3 className="heading-4">
                      {role.title}
                    </h3>
                  </div>
                  <p className="body-medium mb-4">
                    {role.description}
                  </p>
                  <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg">
                    <h4 className="label-large mb-2">
                      Wymagania:
                    </h4>
                    <ul className="space-y-2">
                      {role.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-accessible-red-600 flex-shrink-0" />
                          <span className="body-medium no-margin">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="mt-16 bg-accessible-red-600 rounded-lg p-8 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="heading-2-white mb-4">
              Chcesz dołączyć do naszego zespołu?
            </h2>
            <p className="body-medium-white-90">
              Skontaktuj się z nami i dowiedz się więcej o możliwościach wolontariatu
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="bg-white/10 p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6" />
                <h3 className="label-large-white">Zespół</h3>
              </div>
              <p className="body-medium-white-90">
                Dołącz do zespołu 30+ aktywnych wolontariuszy
              </p>
            </div>
            
            <div className="bg-white/10 p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6" />
                <h3 className="label-large-white">Elastyczność</h3>
              </div>
              <p className="body-medium-white-90">
                Dopasuj grafik do swoich możliwości
              </p>
            </div>
            
            <div className="bg-white/10 p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="w-6 h-6" />
                <h3 className="label-large-white">Szkolenia</h3>
              </div>
              <p className="body-medium-white-90">
                Zapewniamy wprowadzenie i wsparcie
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <a 
              href="tel:+48664821160"
              className="flex items-center gap-4 bg-white/10 p-6 rounded-lg hover:bg-white/20 transition-all duration-300 transform hover:scale-[1.02]"
            >
              <Phone className="w-8 h-8" />
              <div>
                <p className="label-large-white">Zadzwoń</p>
                <p className="body-medium-white-90">664 821 160</p>
              </div>
            </a>
            
            <a 
              href="mailto:znajdki2012@gmail.com"
              className="flex items-center gap-4 bg-white/10 p-6 rounded-lg hover:bg-white/20 transition-all duration-300 transform hover:scale-[1.02]"
            >
              <Mail className="w-8 h-8" />
              <div>
                <p className="label-large-white">Napisz</p>
                <p className="body-medium-white-90">znajdki2012@gmail.com</p>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-16">
        <h2 className="heading-2 text-center mb-8">
          Często zadawane pytania
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <h3 className="heading-4 mb-4">
              Ile czasu muszę poświęcić?
            </h3>
            <p className="body-medium">
              Minimalny czas to 4 godziny tygodniowo, ale możesz zaangażować się bardziej. 
              Grafik ustalamy indywidualnie, biorąc pod uwagę Twoje możliwości.
            </p>
          </div>
          
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <h3 className="heading-4 mb-4">
              Czy potrzebuję doświadczenia?
            </h3>
            <p className="body-medium">
              Nie jest wymagane wcześniejsze doświadczenie. Zapewniamy szkolenie i wsparcie 
              doświadczonych wolontariuszy.
            </p>
          </div>
          
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <h3 className="heading-4 mb-4">
              Czy muszę mieć ukończone 18 lat?
            </h3>
            <p className="body-medium">
              Przyjmujemy wolontariuszy od 16 roku życia. Osoby niepełnoletnie potrzebują 
              pisemnej zgody rodziców lub opiekunów prawnych.
            </p>
          </div>
          
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <h3 className="heading-4 mb-4">
              Jak wygląda proces rekrutacji?
            </h3>
            <p className="body-medium">
              Po kontakcie zapraszamy na spotkanie wprowadzające, gdzie poznasz zespół i zasady 
              pracy. Następnie przechodzisz krótkie szkolenie i możesz zacząć pomagać.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}